import { StorageKeys } from '../constants/constants';
import SmgToken from '../models/smg-token';
import Cookies from 'universal-cookie';
import { isSafeHttpsUrl, isValidIdentifier, toTrustedSmgAuthBase } from './smg-auth-helper';

function getMsSsoToken(): string {
  return getCookie(StorageKeys.msTeamsToken) as string;
}

function setMsSsoToken(msSsoToken: string) {
  setCookie(StorageKeys.msTeamsToken, msSsoToken);
}

function removeMsSsoToken(): void {
  removeCookie(StorageKeys.msTeamsToken);
}

function getSmgToken(): SmgToken {
  let smgAccessToken = getCookie(StorageKeys.smgAccessToken);
  let smgRefreshToken = getCookie(StorageKeys.smgRefreshToken);
  return {
    accessToken: smgAccessToken,
    refreshToken: smgRefreshToken,
  };
}

function getSmgAccessToken(): string {
  return getCookie(StorageKeys.smgAccessToken);
}

function getSmgRefreshToken(): string {
  return getCookie(StorageKeys.smgRefreshToken);
}

function setSmgToken(smgToken: SmgToken) {
  setCookie(StorageKeys.smgAccessToken, smgToken.accessToken);
  setCookie(StorageKeys.smgRefreshToken, smgToken.refreshToken);
}

function removeSmgToken() {
  removeCookie(StorageKeys.smgAccessToken);
  removeCookie(StorageKeys.smgRefreshToken);
}

function getGrantMsgDisply(): string {
  return getCookie(StorageKeys.grantMsgDisplay);
}

function setGrantMsgDisply() {
  setCookie(StorageKeys.grantMsgDisplay, true);
}

function removeGrantMsgDisply() {
  removeCookie(StorageKeys.grantMsgDisplay);
}

function saveRealm(realm: string | null) {
  if (isValidIdentifier(realm)) {
    setCookie(StorageKeys.realm, realm);
  } else if (realm) {
    warnRejectedUrlParameter('realm', realm);
  }
}

function getRealm(): string {
  return getCookie(StorageKeys.realm) as string;
}

function saveClient(client: string | null) {
  if (isValidIdentifier(client)) {
    setCookie(StorageKeys.client, client);
  } else if (client) {
    warnRejectedUrlParameter('client', client);
  }
}

function getClient(): string {
  return getCookie(StorageKeys.client) as string;
}

function saveAuthBase(authBase: string | null) {
  const trustedAuthBase = toTrustedSmgAuthBase(authBase);
  if (trustedAuthBase) {
    setCookie(StorageKeys.smgAuthBase, trustedAuthBase);
  } else if (authBase) {
    warnRejectedUrlParameter('smgAuthBase', authBase);
  }
}

// Cookies written by older versions were never validated, so a stored value is checked again and dropped if untrusted.
function getAuthBase(): string | null {
  const storedAuthBase = getCookie(StorageKeys.smgAuthBase) as string | undefined;
  const trustedAuthBase = toTrustedSmgAuthBase(storedAuthBase);
  if (storedAuthBase && !trustedAuthBase) {
    removeAuthBase();
  }
  return trustedAuthBase;
}

function removeAuthBase() {
  removeCookie(StorageKeys.smgAuthBase);
}

function saveContactUrl(contactUrl: string | null) {
  if (isSafeHttpsUrl(contactUrl)) {
    setCookie(StorageKeys.contactUrl, contactUrl);
  }
}

function getContactUrl(): string | undefined {
  const contactUrl = getCookie(StorageKeys.contactUrl) as string | undefined;
  return isSafeHttpsUrl(contactUrl) ? contactUrl : undefined;
}

// Rejected values are not stored, so login would otherwise fail without any hint of the cause.
function warnRejectedUrlParameter(name: string, value: string) {
  console.warn(`Ignoring ${name} URL parameter with unsupported value: ${value}`);
}

function setCookie(cookieName: string, cookieValue: any) {
  const cookies = new Cookies();
  cookies.set(cookieName, cookieValue, {
    secure: true, sameSite: 'none', path: '/', expires: new Date(new Date().getTime() + 60 * 60 * 1000 * 24 * 365) });
}

function getCookie(cookieName: string) {
  const cookies = new Cookies();
  return cookies.get(cookieName);
}

function removeCookie(cookieName: string) {
  const cookies = new Cookies();
  return cookies.remove(cookieName, {secure: true, sameSite: 'none', path: '/' });
}

export {
  getMsSsoToken,
  setMsSsoToken,
  removeMsSsoToken,
  getSmgToken,
  getSmgAccessToken,
  getSmgRefreshToken,
  setSmgToken,
  removeSmgToken,
  saveRealm,
  getRealm,
  saveClient,
  getClient,
  saveAuthBase,
  getAuthBase,
  removeAuthBase,
  saveContactUrl,
  getContactUrl,
  getGrantMsgDisply,
  setGrantMsgDisply,
  removeGrantMsgDisply,
};
